import { endAdornments } from '@blocks/mortgage-simulator/utils'
import IconEuro from '@fortuneo/ui-icons/general/IconEuro'
import { yupResolver } from '@hookform/resolvers/yup'
import Button from '@ui/atoms/button'
import { Dropdown } from '@ui/atoms/dropdown'
import { TextField } from '@ui/atoms/text-field/textfield'
import { Card } from '@ui/card/card'
import { omit, range } from '@utils/utils'
import React, { type FC, useCallback, useState } from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import {
  getMortgageMonthly,
  type GetMortgageMonthlyResponse,
} from '../../../../../services/api'
import { errorMessages } from '../error.messages'
import { SimulatorResponse } from '../simulator-response'
import { SimulatorResponseFailure } from '../simulator-response-failure'

type FormInput = Partial<{
  duration: number
  income: number
  contributionAmount: number
  projectAmount: number
}>

const validationSchema = Yup.object({
  projectAmount: Yup.number()
    .required(errorMessages.EMPTY)
    .typeError(errorMessages.INVALID_ENTRY)
    .positive(errorMessages.MIN_VALUE)
    .min(0, errorMessages.MIN_VALUE),
  income: Yup.number()
    .required(errorMessages.EMPTY)
    .typeError(errorMessages.INVALID_ENTRY)
    .positive(errorMessages.MIN_VALUE)
    .min(0, errorMessages.MIN_VALUE),
  contributionAmount: Yup.number()
    .required(errorMessages.EMPTY)
    .typeError(errorMessages.INVALID_ENTRY)
    .positive(errorMessages.MIN_VALUE)
    .min(0, errorMessages.MIN_VALUE),
  duration: Yup.number()
    .required(errorMessages.EMPTY)
    .typeError(errorMessages.INVALID_ENTRY)
    .min(7, errorMessages.INVALID_DURATION)
    .max(25, errorMessages.INVALID_DURATION),
})

const numberFormatEuro = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
})

export const SimulatorMonthlyPayment: FC = () => {
  const [rate, setRate] = useState<string>()
  const [monthlyPaymentAmount, setMonthlyPaymentAmount] = useState<string>()
  const [simulationFailure, setSimulationFailure] = useState<boolean>(false)

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  })

  const onSubmit: SubmitHandler<FormInput> = useCallback(
    async (data) => {
      const { duration, income, contributionAmount, projectAmount } = data
      try {
        const response = await getMortgageMonthly(
          duration * 12,
          income,
          contributionAmount,
          projectAmount
        )
        const responseData: GetMortgageMonthlyResponse = await response.json()
        setRate(responseData.rate.toFixed(2))
        setMonthlyPaymentAmount(
          numberFormatEuro.format(responseData.monthlyPaymentAmount)
        )
        if (simulationFailure) {
          setSimulationFailure(false)
        }
      } catch (error) {
        setRate(undefined)
        setMonthlyPaymentAmount(undefined)
        setSimulationFailure(true)
      }
    },
    [simulationFailure]
  )

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 laptop:col-span-5">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <div className="p-5 tablet:p-10">
              <div className="flex items-center gap-5 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#BDBDBD]">
                  <IconEuro className="fill-background-default" />
                </div>
                <span className="text-085">Calculer votre mensualité</span>
              </div>
              <div className="my-4 flex flex-col gap-5">
                <Controller
                  control={control}
                  name="projectAmount"
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      required
                      endAdornments={endAdornments}
                      helper={errors.projectAmount?.message}
                      label="Montant du prêt"
                      type="number"
                      validation={errors.projectAmount ? 'error' : undefined}
                      {...omit(field, ['ref'])}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="contributionAmount"
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      required
                      endAdornments={endAdornments}
                      helper={errors.contributionAmount?.message}
                      label="Apport personnel"
                      type="number"
                      validation={
                        errors.contributionAmount ? 'error' : undefined
                      }
                      {...omit(field, ['ref'])}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="income"
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      required
                      endAdornments={endAdornments}
                      helper={errors.income?.message}
                      label="Revenus mensuels nets avant impôt"
                      type="number"
                      validation={errors.income ? 'error' : undefined}
                      {...omit(field, ['ref'])}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="duration"
                  render={({ field }) => (
                    <Dropdown
                      fullWidth
                      required
                      className="flex-col"
                      helper={errors.duration?.message}
                      label="Durée souhaitée"
                      validation={errors.duration ? 'error' : undefined}
                      {...omit(field, ['ref'])}
                    >
                      {range(7, 26).map((n) => (
                        <option
                          key={`capacity-range-item-${n}-ans`}
                          label={`${n} ans`}
                          value={n}
                        ></option>
                      ))}
                    </Dropdown>
                  )}
                />
              </div>
              <div className="flex justify-center pt-10">
                <Button type="submit">Calculer</Button>
              </div>
            </div>
          </Card>
        </form>
      </div>
      {Boolean(rate) && Boolean(monthlyPaymentAmount) && (
        <div className="col-span-12 laptop:col-span-7">
          <SimulatorResponse
            values={[
              {
                label: 'Mensualité hors assurance',
                value: monthlyPaymentAmount,
              },
              { label: 'Taux nominal annuel fixe', value: `${rate} %` },
            ]}
          />
        </div>
      )}
      {simulationFailure && (
        <div className="col-span-12 laptop:col-span-7">
          <SimulatorResponseFailure />
        </div>
      )}
    </div>
  )
}
